import {HttpClient, HttpEventType, HttpParams} from '@angular/common/http';
import {Injectable, InjectionToken} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {IAuditInterface} from '../../../../../global/audit.interface';
import {MyImportsStatusEnum} from '../../../my-imports/store/my-imports.dto';
import {IContractControlData} from '../../contract-control.dto';
import {ImportFile, ImportFileType, ImportSettingsData} from '../../get-imports.dto';
import {IBulkOptionDLLItemsDTO, ICategorySummaryEntity, IContractCardsSummaryEntity, IImportPDFMapping, IOrganizationSummaryEntity} from './steps/match-contract-cards-step/match-contract-cards-step.component';

export interface IUploadFilesService {
    uploadFile<T,P>(file: File, requestParams:P): Observable<IProgressResponse<T>>
}

export interface ICCBulkUploadFilesService extends IUploadFilesService{
    merge_organizations:boolean;

    getExampleFileUrls(): Observable<IGetExampleFileUrlsResponseDTO>

    getPotentialMapping(importId: number): Observable<IGetPotentialMappingResponseDTO>

    getFirstLine(importId: number, firstLineTitles: 0 | 1): Observable<IFirstLineTitlesResponseDTO>

    uploadXLSFile(file: File, documentTypeId: number): Observable<IUploadXLSFileResponseDTO>;

    getImportPreview(importId: number, firstLineTitles: boolean): Observable<IImportPreviewResponseDTO>;

    importXls(importId: number, firstLineTitles: 0 | 1, merge_organizations: boolean): Observable<IImportResponseDTO>;

    saveMapping(importId: number, mappings: string[]): Observable<ISaveMappingResponseDTO>;

    createImport(): Observable<IUploadXLSFileResponseDTO>;

    importPDF(uploadId: number): Observable<IPreviewPDFImportResponseDTO>

    setImportSettings(importId:number, setImportSettingsRequest: ISetImportSettingsRequest):Observable<ISetImportSettingsResponse>
    
    exportFailedData(importId: number): Observable<Blob>;

}

export interface IPreviewPDFMappedFiles {
    contract: Partial<IContractControlData>
    file: {
        id: number,
        import_id: number,
        type: string,
        original_file_name: string,
        file_size: number,
        file_path: string,
        status: string,
        additional_metadata?: { [key: string]: IContractControlData },
        created_at: Date,
        updated_at: Date
    }
}

export interface IImportStatistics{
    valid_count?:number,
    invalid_count?: number
}

export interface IPreviewPDFImportResponseDTO { 
    categories:ICategorySummaryEntity[],
    organizations:IOrganizationSummaryEntity[],
    contract_controls:IContractCardsSummaryEntity[]
}

export interface IImportResponseDTO {

    stats: {
        fully_imported_count:number,
        partially_imported_count: number,
        none_imported_count:number,
        not_imported_count: number,
    },
    pdf_matching: IPDFMatchingDTO[],
    options:{
        document_types: IBulkOptionDLLItemsDTO[],
        categories: IBulkOptionDLLItemsDTO[],
        contract_controls: IBulkOptionDLLItemsDTO[]
    },

}

export interface IPDFMatchingDTO {
    filename: string,
    document_type_ids: number[],
    category_ids: number[],
    contract_control_ids: number[]
}



export interface ISaveMappingResponseDTO {}

export interface IImportPreviewResponseDTO {
    
    rows_data:[
        {
            index: number,
            checksum:string,
            mapped_data:[
                {
                    label: string,
                    column: string,
                    value: string
                }
            ],
            warnings?: {
                [key: string]: string[]
            },
            errors?: {
                [key: string]: string[]
            },
        }
        
    ],
    
    stats:{
        valid_count: number,
        invalid_count: number
    }
}

export interface IProgressResponse<T = unknown> {
    percent: number,
    response: T,
    file: File
}

export interface IUploadSettingsResponse{
    settings:{
        first_line_titles: boolean,
        merge_organizations: boolean,
        todo:IToDoSetImportSettingsRequest|null
    }

}

export interface IUploadXLSFileResponseDTO extends IUploadSettingsResponse{
    id: number
}

export interface IGetExampleFileUrlsResponseDTO {
    csv: string;
    xls: string;
}

export interface IGetPotentialMappingDetailsResponseDTO {
    [item: string]: IGetPotentialMappingDetailsItemResponseDTO
}

export interface IGetPotentialMappingDetailsItemResponseDTO {
    column: string,
    description: string,
    label: string,
    type: string
}

export enum IGetPotentialMappingGroupRequiredEnum {
    category_name = 'category_name',
    contract_type = 'contract_type',
    contract_name = 'contract_name',
    contract_no = 'contract_no',
    contract_status = 'contract_status',
    contract_import_filename = 'contract_import_filename',
    company_name = 'company_name',
    company_register_no = 'company_register_no',
    company_address = 'company_address',
    company_zip_code = 'company_zip_code',
    company_city = 'company_city',
    cost_amount = 'cost_amount',
    cost_currency ='cost_currency',
    cost_period ='cost_period',
    note_comment ='note_comment',
    recipient_address = 'recipient_address',
    recipient_city = 'recipient_city' ,
    recipient_email = 'recipient_email' ,
    recipient_firstname = 'recipient_firstname' ,
    recipient_lastname = 'recipient_lastname' ,
    recipient_personal_no = 'recipient_personal_no' ,
    recipient_phone = 'recipient_phone' ,
    recipient_zip_code = 'recipient_zip_code' ,
    renewal_notice_period_length = 'renewal_notice_period_length' ,
    renewal_period_length = 'renewal_period_length' ,
    revenue_amount = 'revenue_amount' ,
    revenue_currency = 'revenue_currency' ,
    revenue_period = 'revenue_period' 
}

export enum IGetPotentialMappingGroupOptionalEnum {
    metadata_checkbox_doc_field = 'metadata_checkbox_doc_field',
    metadata_multi_choice_doc_field = 'metadata_multi_choice_doc_field',
    metadata_single_choice_doc_field = 'metadata_single_choice_doc_field',
    metadata_text_doc_field =  'metadata_text_doc_field',
}

export type IGetPotentialMappingGroupEnum  =  IGetPotentialMappingGroupRequiredEnum & IGetPotentialMappingGroupOptionalEnum;

export interface IGetPotentialMappingItemDTO{
    column: string,
    description: string,
    label: string,
    type: string,
    is_required: boolean
}

export interface IGetPotentialMappingResponseDTO {
    [field: keyof(IGetPotentialMappingGroupEnum)]: IGetPotentialMappingItemDTO
}

export interface IFirstLineTitlesResponseDTO {
    title_row: string[],
    data_row: string[]
}

export interface IImportFileRequestParams{
    importId: number
}

export interface  IToDoSetImportSettingsRequest {
    /**
     * Message to set in the action
     */
    description: string;
    /**
     * Due date for newly created action (7 days by default)
     */
    due_date: null | string;
    /**
     * Id of the user to be assigned to the action. (Current user by default)
     */
    user_id: number | null;
}

export interface ISetImportSettingsRequest{

    /**
     * Indicates that the first line of the CSV file is to be ignored.
     */
    first_line_titles: boolean;
    /**
     * If true, the endpoint will attempt to find organizations in the system and assign
     * contracts to them. In case it's set to false it will create a new organization for each
     * row.
     */
    merge_organizations: boolean;
    /**
     * Parameters for automatic creation of Actions for imported rows.
     */
    todo:IToDoSetImportSettingsRequest
}

export interface ISetImportSettingsResponse extends IAuditInterface{
    files?: ImportFile[];
    id: number;
    identifier: string;
    settings: ImportSettingsData;
    status: MyImportsStatusEnum;
    type: ImportFileType;
}

export const IUploadFilesServiceToken = new InjectionToken<IUploadFilesService>('IUploadFilesServiceToken');

@Injectable({
    providedIn: 'root'
})
export class UploadFilesService implements ICCBulkUploadFilesService {
    private _merge_organizations: boolean;

    constructor(private httpClient: HttpClient) {}

    exportFailedData(importId: number): Observable<Blob> {
        return this.httpClient.post<Blob>(`imports/contracts/${importId}/export-invalid`, {} , {responseType: 'blob' as 'json'} );
    }

    set merge_organizations(merge_organizations: boolean){
        this._merge_organizations = merge_organizations;
    }
    get merge_organizations(){
        return this._merge_organizations;
    }

    createImport(): Observable<IUploadXLSFileResponseDTO> {

        return this.httpClient.get<IUploadXLSFileResponseDTO>('imports/contracts/start-import', {});
    }

    saveMapping(importId: number, mappings: string[]): Observable<ISaveMappingResponseDTO> {
        return this.httpClient.post<ISaveMappingResponseDTO>(`imports/contracts/${importId}/mappings`, {
            mappings: mappings
        });
    }

    importXls(importId: number, firstLineTitles: 0 | 1, merge_organizations: boolean): Observable<IImportResponseDTO> {
        return this.httpClient.post<IImportResponseDTO>(`imports/contracts/${importId}/process-import`, {
            first_line_titles: firstLineTitles ?? 0,
            merge_organizations: merge_organizations
        });
    }

    getImportPreview(importId: number, firstLineTitles: boolean): Observable<IImportPreviewResponseDTO> {
        return this.httpClient.post<IImportPreviewResponseDTO>(`imports/contracts/${importId}/preview-validate`, {
            first_line_titles: firstLineTitles
        });

    }

    uploadXLSFile(file: File, documentTypeId:number): Observable<IUploadXLSFileResponseDTO> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type_id', `${documentTypeId}`);

        return this.httpClient.post<IUploadXLSFileResponseDTO>('imports/contracts/upload', formData);
    }

    getFirstLine(importId: number, firstLineTitles: 0 | 1 = 0): Observable<IFirstLineTitlesResponseDTO> {
        const httpParams = new HttpParams({fromObject: {first_line_titles: firstLineTitles ? 1 : 0}})

        return this.httpClient.get<IFirstLineTitlesResponseDTO>(`imports/contracts/${importId}/first-line-data`, {
            params: httpParams
        });
    }

    getPotentialMapping(importId: number): Observable<IGetPotentialMappingResponseDTO> {
       return this.httpClient.get<IGetPotentialMappingResponseDTO>(`imports/contracts/${importId}/mappings`)
    }


    getExampleFileUrls(): Observable<IGetExampleFileUrlsResponseDTO> {
        return this.httpClient.get<IGetExampleFileUrlsResponseDTO>('imports/contracts/example-urls');
    }


    uploadFile<T, P>(file: File, requestParams: P ): Observable<IProgressResponse<T>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type_id', (<{document_type_id:number}>requestParams).document_type_id.toString());
        const ccIds = (<{contract_card_ids:number[]}>requestParams).contract_card_ids;
        if(ccIds && ccIds.length > 0){
            ccIds.forEach(element => {
                formData.append('contract_control_ids[]', element.toString());
             })
        }
        
       

        const categoryIds = (<{category_ids:number[]}>requestParams).category_ids;
        if (categoryIds && categoryIds.length > 0) {
            categoryIds.forEach(id => {
                formData.append('category_ids[]', id.toString());
            });
        }


        return this.httpClient.post<IProgressResponse<T>>(`imports/contracts/${(requestParams as unknown as IImportFileRequestParams).importId}/pdf/upload`,
            formData,
            {
                observe: 'events',
                reportProgress: true
            }
        )
            .pipe(map(event => {
                switch (event.type) {
                    case HttpEventType.Sent:
                        return {
                            percent: 0,
                            file: file
                        } as IProgressResponse<T>;
                    case HttpEventType.UploadProgress: {
                        return {
                            percent: event.total ? Math.round(100 * event.loaded / event.total) : 0,
                            file: file
                        } as IProgressResponse<T>
                    }

                    case HttpEventType.Response: {
                        return {
                            percent: 100,
                            response: event.body,
                            file: file
                        } as IProgressResponse<T>
                    }
                }
                return {
                    file: file,
                    percent: 0
                } as IProgressResponse<T>;
            }));
    }

    importPDF(importId: number): Observable<IPreviewPDFImportResponseDTO> {
        return this.httpClient.post<IPreviewPDFImportResponseDTO>(`imports/contracts/${importId}/finalize`, {
            type: "contract_name"
        });
    }

    setImportSettings(importId:number, setImportSettingsRequest: ISetImportSettingsRequest){
        return this.httpClient.post<ISetImportSettingsResponse>(`imports/contracts/${importId}/settings`, setImportSettingsRequest)
    }
}


